import { apiUrl } from "../api/endpoints";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import SettingsInputAntennaOutlinedIcon from "@mui/icons-material/SettingsInputAntennaOutlined";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import { IconButton, Menu, MenuItem } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import { useAuth } from "../hooks/AuthProvider";
import Spinner from "../Components/Spinner";
import Alert from "../Components/Alert";
import { getErrorMessage } from "../utils/errorUtils";
import { LanguageContext } from "../context/LanguageContext";
import subscriptionTranslations from "../languages/subscription.json";
import subscriptionDetailsExtraTranslations from "../languages/subscriptionDetailsExtra.json";

import "./SubscriptionDetails.css";

const PLAN_FILTER_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "expiring_soon", label: "Expiring Soon" },
  { value: "expired", label: "Expired" },
  { value: "pending_activation", label: "Pending" },
  { value: "disabled", label: "Disabled" },
];

const CONNECTION_FILTER_OPTIONS = [
  { value: "online", label: "Online" },
  { value: "offline", label: "Offline" },
];

const toIsoDateStart = (dateStr) => new Date(`${dateStr}T00:00:00.000Z`).toISOString();
const toIsoDateEnd = (dateStr) => new Date(`${dateStr}T23:59:59.999Z`).toISOString();
const toStartOfDay = (dateStr) => {
  if (!dateStr) return null;
  const parsed = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};
const toEndOfDay = (dateStr) => {
  if (!dateStr) return null;
  const parsed = new Date(`${dateStr}T23:59:59.999`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};
const parseDateOrNull = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year}, ${hours}:${minutes}`;
};

const formatDateShort = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const normalizeLifecycleState = (device) => {
  const direct = String(device?.lifecycle_state || "").trim().toLowerCase();
  if (direct) return direct;
  const status = String(device?.plan_status || "").trim().toLowerCase();
  if (status === "pending_activation") return "pending_activation";
  if (status === "disabled") return "disabled";
  if (status === "expired") return "expired";
  return "active";
};

const isOnlineDevice = (device) =>
  String(device?.status || "")
    .trim()
    .toLowerCase() === "online";

export const SubscriptionDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useContext(LanguageContext);
  const { user, permissions } = useAuth();
  const translationRoot = subscriptionTranslations[lang] || subscriptionTranslations.en;
  const pageBaseT = translationRoot.subscriptionDetails || {};
  const pageExtraT =
    subscriptionDetailsExtraTranslations[lang] || subscriptionDetailsExtraTranslations.en;
  const detailsT = {
    ...pageBaseT,
    ...pageExtraT,
    actions: {
      ...(pageBaseT.actions || {}),
      ...(pageExtraT.actions || {}),
    },
    filterPopup: {
      ...(pageBaseT.filterPopup || {}),
      ...(pageExtraT.filterPopup || {}),
    },
    extendPopup: {
      ...(pageBaseT.extendPopup || {}),
      ...(pageExtraT.extendPopup || {}),
    },
    status: {
      ...(pageBaseT.status || {}),
      ...(pageExtraT.status || {}),
    },
    deviceMeta: {
      ...(pageBaseT.deviceMeta || {}),
      ...(pageExtraT.deviceMeta || {}),
    },
    fields: {
      ...(pageBaseT.fields || {}),
      ...(pageExtraT.fields || {}),
    },
    alerts: {
      ...(pageBaseT.alerts || {}),
      ...(pageExtraT.alerts || {}),
    },
    summary: {
      ...(pageExtraT.summary || {}),
    },
  };
  const isSuperUser = String(user?.role || "").toLowerCase() === "super";
  const canViewSubscriptions = permissions["view_subscriptions"];
  const canEditSubscription = permissions["edit_subscription"];
  const canManagePlans = isSuperUser && permissions["provision_new_devices"];

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [subscriber, setSubscriber] = useState(null);
  const [subscriberForm, setSubscriberForm] = useState({
    id: Number(id) || 0,
    name: "",
    email: "",
    phone: "",
    application: "",
    max_devices: 1,
    max_templates: 1,
    max_datastream_per_template: 1,
    max_users: 1,
  });
  const [devices, setDevices] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [filters, setFilters] = useState({
    planEnabled: false,
    planStatuses: [],
    connectionEnabled: false,
    connectionStatuses: [],
    searchEnabled: false,
    createdFrom: "",
    createdTo: "",
    expiredFrom: "",
    expiredTo: "",
  });
  const [filterPopup, setFilterPopup] = useState({
    open: false,
    search: "",
    planEnabled: false,
    planStatuses: [],
    connectionEnabled: false,
    connectionStatuses: [],
    searchEnabled: false,
    createdFrom: "",
    createdTo: "",
    expiredFrom: "",
    expiredTo: "",
  });
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [deviceActionsMenu, setDeviceActionsMenu] = useState({
    anchorEl: null,
    deviceId: null,
  });
  const [extendPopup, setExtendPopup] = useState({
    open: false,
    mode: "extend",
    fromDate: "",
    toDate: "",
    targetDeviceIds: [],
  });
  const [alertState, setAlertState] = useState({
    open: false,
    message: "",
    type: "info",
    position: ["ar", "ur"].includes(lang) ? "left" : "right",
  });

  const showAlert = useCallback((type, message) => {
    setAlertState({
      open: true,
      message,
      type,
      position: ["ar", "ur"].includes(lang) ? "left" : "right",
    });
  }, [lang]);

  const getLifecycleLabel = useCallback(
    (lifecycle) => {
      if (lifecycle === "pending_activation") return detailsT.status.pending;
      if (lifecycle === "expiring_soon") return detailsT.status.expiringSoon;
      if (lifecycle === "disabled") return detailsT.status.disabled;
      if (lifecycle === "expired") return detailsT.status.expired;
      return detailsT.status.active;
    },
    [detailsT.status]
  );

  const toPositiveInt = useCallback((value, fallback = 1) => {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed <= 0) return fallback;
    return parsed;
  }, []);

  const buildSubscriberForm = useCallback(
    (source) => ({
      id: Number(source?.id || id) || 0,
      name: source?.name || "",
      email: source?.email || "",
      phone: source?.phone || "",
      application: source?.application || "",
      max_devices: toPositiveInt(source?.max_devices, 1),
      max_templates: toPositiveInt(source?.max_templates, 1),
      max_datastream_per_template: toPositiveInt(source?.max_datastream_per_template, 1),
      max_users: toPositiveInt(source?.max_users, 1),
    }),
    [id, toPositiveInt]
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearchDebounced(searchText.trim());
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchText]);

  const fetchDetails = useCallback(async () => {
    if (!canViewSubscriptions || !isSuperUser) return;

    setLoading(true);
    try {
      const params = {
        plan_status: "all",
        connection_status: "all",
        expiry: "all",
      };
      if (filters.searchEnabled && searchDebounced) params.search = searchDebounced;

      const res = await axios.get(apiUrl(`subscriber/${id}/details`), { params });
      const payload = res?.data?.data || {};
      const subscriberData = payload.subscriber || null;
      setSubscriber(subscriberData);
      if (!isEditMode) {
        setSubscriberForm(buildSubscriberForm(subscriberData));
      }
      setDevices(Array.isArray(payload.devices) ? payload.devices : []);
    } catch (err) {
      showAlert("error", getErrorMessage(err));
      setSubscriber(null);
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, [buildSubscriberForm, canViewSubscriptions, filters.searchEnabled, id, isEditMode, isSuperUser, searchDebounced, showAlert]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const filteredDevices = useMemo(() => {
    const activePlanSet = new Set(
      (filters.planStatuses || []).map((value) => String(value || "").trim().toLowerCase())
    );
    const activeConnectionSet = new Set(
      (filters.connectionStatuses || [])
        .map((value) => String(value || "").trim().toLowerCase())
    );
    const searchValue = String(searchDebounced || "").trim().toLowerCase();
    const createdFrom = toStartOfDay(filters.createdFrom);
    const createdTo = toEndOfDay(filters.createdTo);
    const expiredFrom = toStartOfDay(filters.expiredFrom);
    const expiredTo = toEndOfDay(filters.expiredTo);

    return (devices || []).filter((device) => {
      const lifecycle = normalizeLifecycleState(device);
      if (filters.planEnabled && activePlanSet.size > 0 && !activePlanSet.has(lifecycle)) {
        return false;
      }

      if (filters.connectionEnabled && activeConnectionSet.size > 0) {
        const connection = isOnlineDevice(device) ? "online" : "offline";
        if (!activeConnectionSet.has(connection)) return false;
      }

      if (filters.searchEnabled && searchValue) {
        const name = String(device?.name || "").toLowerCase();
        const did = String(device?.did || "").toLowerCase();
        if (!name.includes(searchValue) && !did.includes(searchValue)) {
          return false;
        }
      }

      if (createdFrom || createdTo) {
        const createdDate = parseDateOrNull(device?.created);
        if (!createdDate) return false;
        if (createdFrom && createdDate.getTime() < createdFrom.getTime()) return false;
        if (createdTo && createdDate.getTime() > createdTo.getTime()) return false;
      }

      if (expiredFrom || expiredTo) {
        const expiredDate = parseDateOrNull(device?.expired_at);
        if (!expiredDate) return false;
        if (expiredFrom && expiredDate.getTime() < expiredFrom.getTime()) return false;
        if (expiredTo && expiredDate.getTime() > expiredTo.getTime()) return false;
      }

      return true;
    });
  }, [devices, filters.connectionEnabled, filters.connectionStatuses, filters.createdFrom, filters.createdTo, filters.expiredFrom, filters.expiredTo, filters.planEnabled, filters.planStatuses, filters.searchEnabled, searchDebounced]);

  const visibleSummary = useMemo(() => {
    const stats = {
      total: filteredDevices.length,
      active: 0,
      expired: 0,
      disabled: 0,
    };

    filteredDevices.forEach((device) => {
      const lifecycle = normalizeLifecycleState(device);
      if (lifecycle === "expired") {
        stats.expired += 1;
        return;
      }
      if (lifecycle === "disabled") {
        stats.disabled += 1;
        return;
      }
      if (lifecycle === "active" || lifecycle === "expiring_soon") {
        stats.active += 1;
      }
    });

    return stats;
  }, [filteredDevices]);

  useEffect(() => {
    setSelectedDeviceIds((prev) =>
      prev.filter((deviceId) => filteredDevices.some((d) => Number(d.id) === Number(deviceId)))
    );
  }, [filteredDevices]);

  useEffect(() => {
    if (isEditMode) return;
    setSelectedDeviceIds([]);
    setDeviceActionsMenu({ anchorEl: null, deviceId: null });
  }, [isEditMode]);

  const allVisibleSelected =
    filteredDevices.length > 0 && selectedDeviceIds.length === filteredDevices.length;
  const hasSelectedDevices = selectedDeviceIds.length > 0;

  const toggleSelectAll = useCallback(() => {
    if (allVisibleSelected) {
      setSelectedDeviceIds([]);
      return;
    }
    setSelectedDeviceIds(filteredDevices.map((d) => Number(d.id)));
  }, [allVisibleSelected, filteredDevices]);

  const toggleDeviceSelection = useCallback((deviceId) => {
    const idNum = Number(deviceId);
    setSelectedDeviceIds((prev) => {
      if (prev.some((x) => Number(x) === idNum)) {
        return prev.filter((x) => Number(x) !== idNum);
      }
      return [...prev, idNum];
    });
  }, []);

  const startEditMode = useCallback(() => {
    if (!canEditSubscription) {
      showAlert("error", detailsT.alerts.noEditPermission);
      return;
    }
    setSubscriberForm(buildSubscriberForm(subscriber));
    setIsEditMode(true);
  }, [buildSubscriberForm, canEditSubscription, detailsT.alerts.noEditPermission, showAlert, subscriber]);

  const autoEditRequested = Boolean(location?.state?.autoEdit);
  useEffect(() => {
    if (!autoEditRequested || loading) return;

    if (subscriber) {
      startEditMode();
    }

    navigate(location.pathname, { replace: true, state: {} });
  }, [autoEditRequested, loading, subscriber, startEditMode, navigate, location.pathname]);

  const cancelEditMode = useCallback(() => {
    setSubscriberForm(buildSubscriberForm(subscriber));
    setIsEditMode(false);
    setFilterPopup((prev) => ({ ...prev, open: false }));
  }, [buildSubscriberForm, subscriber]);

  const onSubscriberFieldChange = useCallback((field, value) => {
    const numericFields = new Set([
      "max_devices",
      "max_templates",
      "max_datastream_per_template",
      "max_users",
    ]);

    setSubscriberForm((prev) => ({
      ...prev,
      [field]: numericFields.has(field) ? (value === "" ? "" : Number(value)) : value,
    }));
  }, []);

  const saveSubscriberDetails = useCallback(async () => {
    if (!canEditSubscription) {
      showAlert("error", detailsT.alerts.noEditPermission);
      return;
    }

    const payload = {
      ...subscriberForm,
      id: Number(id),
      name: String(subscriberForm.name || "").trim(),
      email: String(subscriberForm.email || "").trim(),
      phone: String(subscriberForm.phone || "").trim(),
      application: String(subscriberForm.application || "").trim(),
      max_devices: toPositiveInt(subscriberForm.max_devices, 1),
      max_templates: toPositiveInt(subscriberForm.max_templates, 1),
      max_datastream_per_template: toPositiveInt(
        subscriberForm.max_datastream_per_template,
        1
      ),
      max_users: toPositiveInt(subscriberForm.max_users, 1),
    };

    if (!payload.name || !payload.email) {
      showAlert("error", detailsT.alerts.nameEmailRequired);
      return;
    }

    setSaving(true);
    try {
      await axios.put(apiUrl(`subscriber`), payload, {
        headers: { "Content-Type": "application/json" },
      });
      showAlert("success", detailsT.alerts.updated);
      setIsEditMode(false);
      await fetchDetails();
    } catch (err) {
      showAlert("error", getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }, [
    canEditSubscription,
    detailsT.alerts.nameEmailRequired,
    detailsT.alerts.noEditPermission,
    detailsT.alerts.updated,
    fetchDetails,
    id,
    showAlert,
    subscriberForm,
    toPositiveInt,
  ]);

  const openFilterPopup = useCallback(() => {
    setFilterPopup({
      open: true,
      search: searchText,
      planEnabled: filters.planEnabled,
      planStatuses: [...filters.planStatuses],
      connectionEnabled: filters.connectionEnabled,
      connectionStatuses: [...filters.connectionStatuses],
      searchEnabled: filters.searchEnabled,
      createdFrom: filters.createdFrom,
      createdTo: filters.createdTo,
      expiredFrom: filters.expiredFrom,
      expiredTo: filters.expiredTo,
    });
  }, [filters.connectionEnabled, filters.connectionStatuses, filters.createdFrom, filters.createdTo, filters.expiredFrom, filters.expiredTo, filters.planEnabled, filters.planStatuses, filters.searchEnabled, searchText]);

  const applyFiltersFromPopup = useCallback(() => {
    const createdFrom = toStartOfDay(filterPopup.createdFrom);
    const createdTo = toEndOfDay(filterPopup.createdTo);
    const expiredFrom = toStartOfDay(filterPopup.expiredFrom);
    const expiredTo = toEndOfDay(filterPopup.expiredTo);

    if (createdFrom && createdTo && createdTo.getTime() < createdFrom.getTime()) {
      showAlert("error", detailsT.alerts.createdDateOrder);
      return;
    }

    if (expiredFrom && expiredTo && expiredTo.getTime() < expiredFrom.getTime()) {
      showAlert("error", detailsT.alerts.expiredDateOrder);
      return;
    }

    setFilters({
      planEnabled: filterPopup.planEnabled,
      planStatuses: [...filterPopup.planStatuses],
      connectionEnabled: filterPopup.connectionEnabled,
      connectionStatuses: [...filterPopup.connectionStatuses],
      searchEnabled: filterPopup.searchEnabled,
      createdFrom: filterPopup.createdFrom,
      createdTo: filterPopup.createdTo,
      expiredFrom: filterPopup.expiredFrom,
      expiredTo: filterPopup.expiredTo,
    });
    setSearchText(filterPopup.search || "");
    setFilterPopup((prev) => ({ ...prev, open: false }));
  }, [detailsT.alerts.createdDateOrder, detailsT.alerts.expiredDateOrder, filterPopup.connectionEnabled, filterPopup.connectionStatuses, filterPopup.createdFrom, filterPopup.createdTo, filterPopup.expiredFrom, filterPopup.expiredTo, filterPopup.planEnabled, filterPopup.planStatuses, filterPopup.search, filterPopup.searchEnabled, showAlert]);

  const clearFiltersFromPopup = useCallback(() => {
    setFilterPopup((prev) => ({
      ...prev,
      search: "",
      planEnabled: true,
      planStatuses: [],
      connectionEnabled: true,
      connectionStatuses: [],
      searchEnabled: true,
      createdFrom: "",
      createdTo: "",
      expiredFrom: "",
      expiredTo: "",
    }));
  }, []);

  const toggleFilterValue = useCallback((key, value) => {
    const normalized = String(value || "").trim().toLowerCase();
    setFilterPopup((prev) => {
      const current = Array.isArray(prev[key]) ? prev[key] : [];
      const exists = current.some(
        (item) => String(item || "").trim().toLowerCase() === normalized
      );
      const next = exists
        ? current.filter(
            (item) => String(item || "").trim().toLowerCase() !== normalized
          )
        : [...current, value];

      return { ...prev, [key]: next };
    });
  }, []);

  const runPlanAction = useCallback(
    async (action, targetIds, extraPayload = {}) => {
      const ids = Array.from(
        new Set(
          (targetIds || [])
            .map((x) => Number(x))
            .filter((x) => Number.isInteger(x) && x > 0)
        )
      );

      if (ids.length === 0) {
        showAlert("warning", detailsT.alerts.selectDevice);
        return false;
      }

      if (!canManagePlans) {
        showAlert("error", detailsT.alerts.noManagePermission);
        return false;
      }

      setActionLoading(action);
      try {
        await axios.post(
          apiUrl(`subscriber/${id}/devices/plan-action`),
          {
            action,
            device_ids: ids,
            ...extraPayload,
          },
          { headers: { "Content-Type": "application/json" } }
        );
        showAlert("success", detailsT.alerts.actionApplied);
        await fetchDetails();
        return true;
      } catch (err) {
        showAlert("error", getErrorMessage(err));
        return false;
      } finally {
        setActionLoading("");
      }
    },
    [canManagePlans, detailsT.alerts.actionApplied, detailsT.alerts.noManagePermission, detailsT.alerts.selectDevice, fetchDetails, id, showAlert]
  );

  const openExtendPopup = useCallback(
    (targetIds, mode = "extend") => {
      const ids = Array.from(
        new Set(
          (targetIds || [])
            .map((x) => Number(x))
            .filter((x) => Number.isInteger(x) && x > 0)
        )
      );

      if (ids.length === 0) {
        showAlert("warning", detailsT.alerts.selectDevice);
        return;
      }

      setExtendPopup({
        open: true,
        mode: mode === "renewal" ? "renewal" : "extend",
        fromDate: "",
        toDate: "",
        targetDeviceIds: ids,
      });
    },
    [detailsT.alerts.selectDevice, showAlert]
  );

  const applyExtend = useCallback(async () => {
    const { fromDate, toDate, targetDeviceIds, mode } = extendPopup;
    if (!toDate) {
      showAlert("error", detailsT.alerts.toDateRequired);
      return;
    }

    if (fromDate && new Date(toDate).getTime() < new Date(fromDate).getTime()) {
      showAlert("error", detailsT.alerts.toDateOrder);
      return;
    }

    const payload = {
      to_date: toIsoDateEnd(toDate),
    };
    if (fromDate) {
      payload.from_date = toIsoDateStart(fromDate);
    }

    const action = mode === "renewal" ? "renew" : "extend";
    const success = await runPlanAction(action, targetDeviceIds, payload);
    if (success) {
      setExtendPopup((prev) => ({ ...prev, open: false }));
    }
  }, [detailsT.alerts.toDateOrder, detailsT.alerts.toDateRequired, extendPopup, runPlanAction, showAlert]);

  const subscriberFields = useMemo(
    () => [
      { key: "name", label: detailsT.fields.name, type: "text" },
      { key: "email", label: detailsT.fields.email, type: "email" },
      { key: "max_devices", label: detailsT.fields.maxDevices, type: "number" },
      { key: "max_templates", label: detailsT.fields.maxTemplates, type: "number" },
      {
        key: "max_datastream_per_template",
        label: detailsT.fields.maxDatastreamPerTemplate,
        type: "number",
      },
      { key: "max_users", label: detailsT.fields.maxUsers, type: "number" },
      { key: "phone", label: detailsT.fields.phone, type: "text" },
      { key: "application", label: translationRoot.columns?.application || "Application", type: "textarea" },
      { key: "registered", label: detailsT.fields.registered, type: "readonly" },
    ],
    [detailsT.fields.email, detailsT.fields.maxDatastreamPerTemplate, detailsT.fields.maxDevices, detailsT.fields.maxTemplates, detailsT.fields.maxUsers, detailsT.fields.name, detailsT.fields.phone, detailsT.fields.registered, translationRoot.columns]
  );

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.planEnabled && filters.planStatuses.length > 0) {
      count += filters.planStatuses.length;
    }
    if (filters.connectionEnabled && filters.connectionStatuses.length > 0) {
      count += filters.connectionStatuses.length;
    }
    if (filters.searchEnabled && searchText.trim()) count += 1;
    if (filters.createdFrom) count += 1;
    if (filters.createdTo) count += 1;
    if (filters.expiredFrom) count += 1;
    if (filters.expiredTo) count += 1;
    return count;
  }, [filters.connectionEnabled, filters.connectionStatuses.length, filters.createdFrom, filters.createdTo, filters.expiredFrom, filters.expiredTo, filters.planEnabled, filters.planStatuses.length, filters.searchEnabled, searchText]);

  const openDeviceActionsMenu = useCallback((event, deviceId) => {
    setDeviceActionsMenu({ anchorEl: event.currentTarget, deviceId: Number(deviceId) });
  }, []);

  const closeDeviceActionsMenu = useCallback(() => {
    setDeviceActionsMenu({ anchorEl: null, deviceId: null });
  }, []);

  if (!canViewSubscriptions || !isSuperUser) {
    return (
      <div className="subscription-details-page">
        <div className="subscription-details-empty">
          {detailsT.emptyPermission}
        </div>
      </div>
    );
  }

  return (
    <div className="configuration subscription-details-page">
      <Alert
        open={alertState.open}
        close={() => setAlertState((s) => ({ ...s, open: false }))}
        message={alertState.message}
        type={alertState.type}
        position={alertState.position}
      />

      <div className="subscription-details-header">
        <div className="subscription-details-header-left">
          <button
            type="button"
            className="subscription-details-back-btn"
            onClick={() => navigate("/administration/subscriptions")}
            aria-label={detailsT.backLabel}
          >
            <ArrowBackIcon fontSize="inherit" />
          </button>

          <div className="subscription-details-heading-text">
            <h2 className="devices-title">{detailsT.pageTitle}</h2>
            <p>{detailsT.pageSubtitle}</p>
          </div>
        </div>

        <div className="subscription-details-header-actions">
          {isEditMode ? (
            <>
              <button
                type="button"
                className="btn btn-cancel-details subscription-details-cancel-btn"
                onClick={cancelEditMode}
                disabled={saving || actionLoading !== ""}
              >
                {detailsT.cancel}
              </button>
              <button
                type="button"
                className="btn asset-edit-main-btn edit-btn-save subscription-details-save-btn"
                onClick={saveSubscriberDetails}
                disabled={saving || actionLoading !== ""}
              >
                {detailsT.saveChanges}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn edit-btn devices-primary-btn subscription-details-edit-toggle"
              onClick={startEditMode}
              disabled={!canEditSubscription}
            >
              <EditOutlinedIcon fontSize="inherit" />
              <span>{detailsT.edit}</span>
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="subscription-details-loading">
          <Spinner />
        </div>
      ) : (
        <div className="subscription-details-content">
          <section className="subscription-details-left">
            <div className="subscription-details-panel-title">{detailsT.panelTitle}</div>
            <div className="subscription-details-grid-fields">
              {subscriberFields.map((field) => (
                <div key={field.label} className="subscription-details-field">
                  <label>{field.label}</label>
                  {isEditMode && field.type !== "readonly" ? (
                    field.type === "textarea" ? (
                      <textarea
                        className="subscription-details-input"
                        rows={3}
                        maxLength={4000}
                        value={subscriberForm[field.key] ?? ""}
                        onChange={(e) => onSubscriberFieldChange(field.key, e.target.value)}
                        style={{ resize: "vertical" }}
                      />
                    ) : (
                      <input
                        type={field.type}
                        className="subscription-details-input"
                        value={subscriberForm[field.key] ?? ""}
                        onChange={(e) => onSubscriberFieldChange(field.key, e.target.value)}
                      />
                    )
                  ) : (
                    <div
                      className="subscription-details-value"
                      style={field.key === "application" ? { whiteSpace: "pre-wrap" } : undefined}
                    >
                      {field.type === "readonly"
                        ? formatDateTime(subscriber?.registered)
                        : subscriber?.[field.key] ?? "-"}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section className="subscription-details-right">
            <div className="subscription-devices-head">
              <h3>{detailsT.devicesTitle}</h3>
              <div className="subscription-devices-summary">
                <span>{detailsT.summary.total.replace("{count}", visibleSummary.total)}</span>
                <span>{detailsT.summary.active.replace("{count}", visibleSummary.active)}</span>
                <span>{detailsT.summary.expired.replace("{count}", visibleSummary.expired)}</span>
                <span>{detailsT.summary.disabled.replace("{count}", visibleSummary.disabled)}</span>
              </div>
            </div>

            <div className="subscription-devices-tools">
              <button
                type="button"
                className="devices-toolbar-btn devices-filter-btn subscription-filter-icon-btn"
                onClick={openFilterPopup}
              >
                <FilterAltOutlinedIcon fontSize="small" />
                <span>{detailsT.filter}</span>
                {activeFiltersCount > 0 ? (
                  <span className="subscription-filter-count">{activeFiltersCount}</span>
                ) : null}
              </button>
            </div>

            {isEditMode && canManagePlans ? (
              <div className="subscription-devices-actions">
                <label className="subscription-select-all">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAll}
                  />
                  <span>{detailsT.selectAll}</span>
                </label>

                <div
                  className={`subscription-actions-buttons ${
                    hasSelectedDevices ? "visible" : "hidden"
                  }`}
                  aria-hidden={!hasSelectedDevices}
                >
                  <button
                    type="button"
                    onClick={() => runPlanAction("enable", selectedDeviceIds)}
                    disabled={actionLoading !== "" || !hasSelectedDevices}
                  >
                    {detailsT.actions.enable}
                  </button>
                  <button
                    type="button"
                    onClick={() => openExtendPopup(selectedDeviceIds, "renewal")}
                    disabled={actionLoading !== "" || !hasSelectedDevices}
                  >
                    {detailsT.actions.renewal}
                  </button>
                  <button
                    type="button"
                    onClick={() => openExtendPopup(selectedDeviceIds, "extend")}
                    disabled={actionLoading !== "" || !hasSelectedDevices}
                  >
                    {detailsT.actions.extend}
                  </button>
                  <button
                    type="button"
                    className="danger"
                    onClick={() => runPlanAction("disable", selectedDeviceIds)}
                    disabled={actionLoading !== "" || !hasSelectedDevices}
                  >
                    {detailsT.actions.disable}
                  </button>
                </div>
              </div>
            ) : null}

            <div className="subscription-devices-list">
              {filteredDevices.length === 0 ? (
                <div className="subscription-devices-empty">
                  {detailsT.emptyDevices}
                </div>
              ) : (
                filteredDevices.map((device) => {
                  const lifecycle = normalizeLifecycleState(device);
                  const online = isOnlineDevice(device);
                  const selected = selectedDeviceIds.some(
                    (x) => Number(x) === Number(device.id)
                  );

                  return (
                    <div
                      key={device.id}
                      className={`subscription-device-card${selected ? " selected" : ""}`}
                    >
                      {isEditMode && canManagePlans ? (
                        <label className="subscription-device-check">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleDeviceSelection(device.id)}
                          />
                        </label>
                      ) : (
                        <div className="subscription-device-check subscription-device-check-placeholder" />
                      )}

                      <div className="subscription-device-main">
                        <div className="subscription-device-title-row">
                          <h4>
                            {device.name ||
                              device.did ||
                              `${detailsT.deviceFallback} #${device.id}`}
                          </h4>
                          {isEditMode && canManagePlans ? (
                            <div className="subscription-device-inline-actions">
                              <IconButton
                                className="subscription-device-menu-btn"
                                onClick={(event) => openDeviceActionsMenu(event, device.id)}
                                size="small"
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </div>
                          ) : null}
                        </div>

                        <div className="subscription-device-meta">
                          {detailsT.deviceMeta.created}: {formatDateShort(device.created)} /{" "}
                          {detailsT.deviceMeta.expiration}:{" "}
                          {formatDateShort(device.expired_at)}
                        </div>

                        <div className="subscription-device-badges">
                          <span className={`subscription-plan-badge state-${lifecycle}`}>
                            {getLifecycleLabel(lifecycle)}
                          </span>
                          <span
                            className={`subscription-connection-badge ${
                              online ? "online" : "offline"
                            }`}
                          >
                            <span className="dot" />
                            {online ? detailsT.status.online : detailsT.status.offline}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </section>
        </div>
      )}

      <Menu
        anchorEl={deviceActionsMenu.anchorEl}
        open={Boolean(deviceActionsMenu.anchorEl)}
        onClose={closeDeviceActionsMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ className: "subscription-device-menu-paper" }}
      >
        <MenuItem
          onClick={() => {
            if (deviceActionsMenu.deviceId) {
              openExtendPopup([deviceActionsMenu.deviceId], "renewal");
            }
            closeDeviceActionsMenu();
          }}
          disabled={actionLoading !== ""}
        >
          <AutorenewOutlinedIcon fontSize="small" />
          <span>{detailsT.actions.renewal}</span>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (deviceActionsMenu.deviceId) {
              openExtendPopup([deviceActionsMenu.deviceId], "extend");
            }
            closeDeviceActionsMenu();
          }}
          disabled={actionLoading !== ""}
        >
          <EventOutlinedIcon fontSize="small" />
          <span>{detailsT.actions.extend}</span>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (deviceActionsMenu.deviceId) {
              runPlanAction("disable", [deviceActionsMenu.deviceId]);
            }
            closeDeviceActionsMenu();
          }}
          disabled={actionLoading !== ""}
          className="danger"
        >
          <BlockOutlinedIcon fontSize="small" />
          <span>{detailsT.actions.disable}</span>
        </MenuItem>
      </Menu>

      {filterPopup.open ? (
        <div id="modal" onClick={() => setFilterPopup((prev) => ({ ...prev, open: false }))}>
          <div
            id="popup"
            className="dashboard-modal subscription-filter-popup"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dashboard-header">
              <div className="dashboard-header-icon subscription-filter-popup-icon">
                <FilterAltOutlinedIcon />
              </div>
              <div className="dashboard-header-text">
                <h2>{detailsT.filterPopup.title}</h2>
                <p>{detailsT.filterPopup.subtitle}</p>
              </div>
            </div>

            <div className="modal-divider" />

            <div className="modal-content dashboard-content">
              <div className="subscription-filter-sections">
                <div className="subscription-filter-group">
                  <div className="subscription-filter-group-head">
                    <div className="subscription-filter-group-title">
                      <AutorenewOutlinedIcon fontSize="small" />
                      <span>{detailsT.filterPopup.planStatuses}</span>
                    </div>
                    <label className="subscription-filter-switch">
                      <input
                        type="checkbox"
                        checked={filterPopup.planEnabled}
                        onChange={(e) =>
                          setFilterPopup((prev) => ({
                            ...prev,
                            planEnabled: e.target.checked,
                          }))
                        }
                      />
                      <span className="subscription-filter-switch-slider" />
                    </label>
                  </div>
                  <div
                    className={`subscription-filter-group-body ${
                      filterPopup.planEnabled ? "expanded" : "collapsed"
                    }`}
                  >
                    {PLAN_FILTER_OPTIONS.map((opt) => {
                      const checked = filterPopup.planStatuses.some(
                        (value) =>
                          String(value || "").trim().toLowerCase() === opt.value
                      );
                      return (
                        <label key={opt.value} className="subscription-filter-option">
                          <span>
                            {opt.value === "active"
                              ? detailsT.status.active
                              : opt.value === "expiring_soon"
                              ? detailsT.status.expiringSoon
                              : opt.value === "expired"
                              ? detailsT.status.expired
                              : opt.value === "pending_activation"
                              ? detailsT.status.pending
                              : detailsT.status.disabled}
                          </span>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleFilterValue("planStatuses", opt.value)}
                            disabled={!filterPopup.planEnabled}
                          />
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="subscription-filter-group">
                  <div className="subscription-filter-group-head">
                    <div className="subscription-filter-group-title">
                      <SettingsInputAntennaOutlinedIcon fontSize="small" />
                      <span>{detailsT.filterPopup.deviceStatuses}</span>
                    </div>
                    <label className="subscription-filter-switch">
                      <input
                        type="checkbox"
                        checked={filterPopup.connectionEnabled}
                        onChange={(e) =>
                          setFilterPopup((prev) => ({
                            ...prev,
                            connectionEnabled: e.target.checked,
                          }))
                        }
                      />
                      <span className="subscription-filter-switch-slider" />
                    </label>
                  </div>
                  <div
                    className={`subscription-filter-group-body ${
                      filterPopup.connectionEnabled ? "expanded" : "collapsed"
                    }`}
                  >
                    {CONNECTION_FILTER_OPTIONS.map((opt) => {
                      const checked = filterPopup.connectionStatuses.some(
                        (value) =>
                          String(value || "").trim().toLowerCase() === opt.value
                      );
                      return (
                        <label key={opt.value} className="subscription-filter-option">
                          <span>
                            {opt.value === "online"
                              ? detailsT.status.online
                              : detailsT.status.offline}
                          </span>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleFilterValue("connectionStatuses", opt.value)}
                            disabled={!filterPopup.connectionEnabled}
                          />
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="subscription-filter-group">
                  <div className="subscription-filter-group-head">
                    <div className="subscription-filter-group-title">
                      <SearchIcon fontSize="small" />
                      <span>{detailsT.filterPopup.deviceName}</span>
                    </div>
                    <label className="subscription-filter-switch">
                      <input
                        type="checkbox"
                        checked={filterPopup.searchEnabled}
                        onChange={(e) =>
                          setFilterPopup((prev) => ({
                            ...prev,
                            searchEnabled: e.target.checked,
                          }))
                        }
                      />
                      <span className="subscription-filter-switch-slider" />
                    </label>
                  </div>
                  <div
                    className={`subscription-filter-group-body ${
                      filterPopup.searchEnabled ? "expanded" : "collapsed"
                    }`}
                  >
                    <div className="subscription-filter-search-wrap">
                      <SearchIcon fontSize="small" />
                      <input
                        type="text"
                        placeholder={detailsT.filterPopup.typeHere}
                        value={filterPopup.search}
                        onChange={(e) =>
                          setFilterPopup((prev) => ({ ...prev, search: e.target.value }))
                        }
                        disabled={!filterPopup.searchEnabled}
                      />
                    </div>
                  </div>
                </div>

                <div className="subscription-filter-group subscription-filter-dates">
                  <div className="subscription-filter-date-row">
                    <div className="subscription-filter-date-label">
                      <span>{detailsT.filterPopup.created}</span>
                    </div>
                    <div className="subscription-filter-date-inputs">
                      <label className="subscription-filter-date-field">
                        <span>{detailsT.filterPopup.from}</span>
                        <input
                          type="date"
                          value={filterPopup.createdFrom}
                          onChange={(e) =>
                            setFilterPopup((prev) => ({
                              ...prev,
                              createdFrom: e.target.value,
                            }))
                          }
                        />
                      </label>
                      <label className="subscription-filter-date-field">
                        <span>{detailsT.filterPopup.to}</span>
                        <input
                          type="date"
                          value={filterPopup.createdTo}
                          onChange={(e) =>
                            setFilterPopup((prev) => ({
                              ...prev,
                              createdTo: e.target.value,
                            }))
                          }
                        />
                      </label>
                    </div>
                  </div>

                  <div className="subscription-filter-date-row">
                    <div className="subscription-filter-date-label">
                      <span>{detailsT.filterPopup.expired}</span>
                    </div>
                    <div className="subscription-filter-date-inputs">
                      <label className="subscription-filter-date-field">
                        <span>{detailsT.filterPopup.from}</span>
                        <input
                          type="date"
                          value={filterPopup.expiredFrom}
                          onChange={(e) =>
                            setFilterPopup((prev) => ({
                              ...prev,
                              expiredFrom: e.target.value,
                            }))
                          }
                        />
                      </label>
                      <label className="subscription-filter-date-field">
                        <span>{detailsT.filterPopup.to}</span>
                        <input
                          type="date"
                          value={filterPopup.expiredTo}
                          onChange={(e) =>
                            setFilterPopup((prev) => ({
                              ...prev,
                              expiredTo: e.target.value,
                            }))
                          }
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer dashboard-footer">
              <button
                type="button"
                className="btn btn-cancel"
                onClick={() => setFilterPopup((prev) => ({ ...prev, open: false }))}
              >
                {detailsT.cancel}
              </button>
              <button type="button" className="btn btn-cancel" onClick={clearFiltersFromPopup} style={{left:'20%'}}>
                {detailsT.reset}
              </button>
              <button
                type="button"
                className="btn btn-apply dashboard-primary-btn"
                onClick={applyFiltersFromPopup}
              >
                {detailsT.filterPopup.apply}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {extendPopup.open ? (
        <div id="modal" onClick={() => setExtendPopup((prev) => ({ ...prev, open: false }))}>
          <div
            id="popup"
            className="dashboard-modal subscription-extend-popup"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dashboard-header">
              <div className="dashboard-header-icon">
                {extendPopup.mode === "renewal" ? (
                  <AutorenewOutlinedIcon />
                ) : (
                  <EventOutlinedIcon />
                )}
              </div>
              <div className="dashboard-header-text">
                <h2>
                  {extendPopup.mode === "renewal"
                    ? detailsT.extendPopup.renewalTitle
                    : detailsT.extendPopup.extendTitle}
                </h2>
                <p>
                  {extendPopup.mode === "renewal"
                    ? detailsT.extendPopup.renewalSubtitle
                    : detailsT.extendPopup.extendSubtitle}
                </p>
              </div>
            </div>

            <div className="modal-divider" />

            <div className="modal-content dashboard-content">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <div className="form-section">
                  <div className="form-item">
                    <label className="form-lbl">{detailsT.extendPopup.from}</label>
                    <DatePicker
                      format="DD/MM/YYYY"
                      value={extendPopup.fromDate ? dayjs(extendPopup.fromDate) : null}
                      onChange={(nextValue) =>
                        setExtendPopup((prev) => ({
                          ...prev,
                          fromDate:
                            nextValue && nextValue.isValid()
                              ? nextValue.format("YYYY-MM-DD")
                              : "",
                        }))
                      }
                      maxDate={extendPopup.toDate ? dayjs(extendPopup.toDate) : undefined}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          placeholder: "DD/MM/YYYY",
                        },
                      }}
                    />
                  </div>

                  <div className="form-item">
                    <label className="form-lbl">{detailsT.extendPopup.to}</label>
                    <DatePicker
                      format="DD/MM/YYYY"
                      value={extendPopup.toDate ? dayjs(extendPopup.toDate) : null}
                      onChange={(nextValue) =>
                        setExtendPopup((prev) => ({
                          ...prev,
                          toDate:
                            nextValue && nextValue.isValid()
                              ? nextValue.format("YYYY-MM-DD")
                              : "",
                        }))
                      }
                      minDate={extendPopup.fromDate ? dayjs(extendPopup.fromDate) : undefined}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          placeholder: "DD/MM/YYYY",
                        },
                      }}
                    />
                  </div>
                </div>
              </LocalizationProvider>
            </div>

            <div className="modal-footer dashboard-footer">
              <button
                type="button"
                className="btn btn-cancel"
                onClick={() => setExtendPopup((prev) => ({ ...prev, open: false }))}
              >
                {detailsT.extendPopup.cancel}
              </button>
              <button
                type="button"
                className="btn btn-apply dashboard-primary-btn"
                onClick={applyExtend}
                disabled={actionLoading !== ""}
              >
                {detailsT.extendPopup.save}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SubscriptionDetails;
